import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Copy, FileText, Package, Archive, Wheat, Apple, Milk, Droplets, Beef, Fish, Soup, Coffee, Wine, ShoppingBasket, Cookie, Salad, Pizza } from 'lucide-react';
import { ArticleAvitaillement, ListeAvitaillement } from '@/hooks/useListesAvitaillement';
import { toast } from '@/components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

interface ListeAchatAvitaillementProps {
  liste: ListeAvitaillement;
  nomBateau: string;
}

const ListeAchatAvitaillement = ({ liste, nomBateau }: ListeAchatAvitaillementProps) => {
  const { t } = useTranslation('inventory');
  const articles = liste.articles;

  const getSousCategorieIcon = (categorie: string) => {
    switch (categorie.toLowerCase()) {
      case 'alimentation':
        return Wheat;
      case 'boissons':
        return Droplets;
      case 'hygiène':
        return Sparkles;
      case 'nettoyage':
        return Archive;
      case 'sécurité':
      case 'navigation':
        return Package;
      case 'confort':
        return Coffee;
      case 'technique':
        return Package;
      case 'vêtements':
        return ShoppingBasket;
      default:
        return Package;
    }
  };

  // Importer Sparkles
  const Sparkles = Package; // Fallback

  const genererListeAchat = () => {
    return articles.map(article => {
      const prioriteLabel = article.priorite === 'critique' ? ' (CRITIQUE)' : '';
      return `${article.quantite} ${article.unite} - ${article.nom}${prioriteLabel}`;
    });
  };

  const copierListeAchat = () => {
    const listeTexte = genererListeAchat();
    if (listeTexte.length === 0) {
      toast({
        title: t('listeAchatAvitaillement.info'),
        description: t('listeAchatAvitaillement.noArticleInList'),
      });
      return;
    }

    const texte = `Liste d'achat - ${liste.nom}\n${liste.nombre_personnes} personnes - ${liste.nombre_jours} jours\n\n` + 
      listeTexte.map(item => `• ${item}`).join('\n');
    navigator.clipboard.writeText(texte).then(() => {
      toast({
        title: t('listeAchatAvitaillement.success'),
        description: t('listeAchatAvitaillement.listCopied'),
      });
    });
  };

  const genererPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const { format } = await import('date-fns');
      const { fr: frLocale, enGB } = await import('date-fns/locale');
      const dateLocale = i18n.language?.startsWith('en') ? enGB : frLocale;
      const doc = new jsPDF();
      
      // Couleurs Nauticoach
      const nauticBlue = [20, 26, 51] as const; // #141A33
      const nauticGold = [232, 163, 6] as const; // #E8A306
      const nauticLight = [34, 45, 88] as const; // #222D58
      const darkGray = [75, 85, 99] as const;
      const criticalRed = [239, 68, 68] as const;
      const lightGray = [243, 244, 246] as const;
      
      // En-tête avec gradient effet
      doc.setFillColor(nauticBlue[0], nauticBlue[1], nauticBlue[2]);
      doc.rect(0, 0, 210, 45, 'F');
      
      // Ligne dorée décorative
      doc.setFillColor(nauticGold[0], nauticGold[1], nauticGold[2]);
      doc.rect(0, 45, 210, 3, 'F');
      
      // Titre
      doc.setTextColor(nauticGold[0], nauticGold[1], nauticGold[2]);
      doc.setFontSize(20);
      doc.text(t('pdf.purchaseListTitle'), 15, 18);
      
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text(liste.nom, 15, 28);
      
      doc.setFontSize(10);
      doc.text(t('pdf.peopleAndDays', { people: liste.nombre_personnes, days: liste.nombre_jours, count: articles.length }), 15, 38);
      
      // Info côté droit
      doc.setTextColor(nauticGold[0], nauticGold[1], nauticGold[2]);
      doc.setFontSize(9);
      doc.text(format(new Date(), 'dd/MM/yyyy', { locale: dateLocale }), 180, 18, { align: 'right' });
      if (nomBateau) {
        doc.setTextColor(255, 255, 255);
        doc.text(nomBateau, 180, 28, { align: 'right' });
      }
      
      const contentTopY = 58;
      const leftColumnX = 15;
      const rightColumnX = 110;
      const columnWidth = 85;
      const checkboxSize = 3.5;

      // Zone utile (laisser de la place au footer)
      const pageBottomY = 282;

      const xByCol = [leftColumnX, rightColumnX] as const;
      const yByCol = [contentTopY, contentTopY];
      let col = 0 as 0 | 1;

      const resetForNewPage = () => {
        doc.addPage();
        yByCol[0] = 25;
        yByCol[1] = 25;
        col = 0;
      };

      const ensureSpace = (neededHeight: number) => {
        if (yByCol[col] + neededHeight <= pageBottomY) return false;
        // Basculer sur la colonne de droite si possible
        if (col === 0 && yByCol[1] + neededHeight <= pageBottomY) {
          col = 1;
          return true;
        }
        // Sinon, nouvelle page
        resetForNewPage();
        return true;
      };

      // Ordre des catégories (priorité d'affichage)
      const ordreCategories = ['alimentation', 'boissons', 'hygiène', 'hygiene'];
      const getCategorieOrdre = (categorie: string): number => {
        const cat = categorie.toLowerCase();
        for (let i = 0; i < ordreCategories.length; i++) {
          if (cat.includes(ordreCategories[i])) return i;
        }
        return 99;
      };
      
      // Fonction pour dessiner une case à cocher
      const drawCheckbox = (x: number, y: number) => {
        doc.setDrawColor(nauticBlue[0], nauticBlue[1], nauticBlue[2]);
        doc.setLineWidth(0.3);
        doc.rect(x, y - 3, checkboxSize, checkboxSize);
      };
      
      // Fonction pour dessiner un article
      const drawArticle = (article: ArticleAvitaillement, x: number, y: number, isCritical: boolean) => {
        drawCheckbox(x, y);
        
        doc.setFontSize(9);
        if (isCritical) {
          doc.setTextColor(criticalRed[0], criticalRed[1], criticalRed[2]);
        } else {
          doc.setTextColor(darkGray[0], darkGray[1], darkGray[2]);
        }
        
        const textX = x + checkboxSize + 3;
        const maxWidth = columnWidth - checkboxSize - 8;
        const text = `${article.quantite} ${article.unite} - ${article.nom}`;
        
        // Tronquer si trop long
        let displayText = text;
        if (doc.getTextWidth(displayText) > maxWidth) {
          while (doc.getTextWidth(displayText + '...') > maxWidth && displayText.length > 0) {
            displayText = displayText.slice(0, -1);
          }
          displayText += '...';
        }
        
        doc.text(displayText, textX, y);
      };
      
      // Sous-catégories pour l'alimentation
      const sousCategories: Record<string, string[]> = {
        'alimentation': ['Fruits & Légumes', 'Viandes & Poissons', 'Produits laitiers', 'Épicerie', 'Conserves', 'Surgelés', 'Boulangerie', 'Snacks'],
        'boissons': ['Eau', 'Sodas', 'Jus', 'Alcools', 'Café & Thé'],
      };
      
      // Fonction pour déterminer la sous-catégorie d'un article
      const getSousCategorie = (article: ArticleAvitaillement): string => {
        const nom = article.nom.toLowerCase();
        if (article.categorie.toLowerCase() === 'alimentation') {
          if (nom.includes('pomme') || nom.includes('banane') || nom.includes('orange') || nom.includes('salade') || nom.includes('tomate') || nom.includes('carotte') || nom.includes('légume') || nom.includes('fruit')) {
            return 'Fruits & Légumes';
          }
          if (nom.includes('poulet') || nom.includes('boeuf') || nom.includes('porc') || nom.includes('poisson') || nom.includes('saumon') || nom.includes('thon') || nom.includes('viande') || nom.includes('jambon') || nom.includes('saucisse')) {
            return 'Viandes & Poissons';
          }
          if (nom.includes('lait') || nom.includes('fromage') || nom.includes('yaourt') || nom.includes('beurre') || nom.includes('crème')) {
            return 'Produits laitiers';
          }
          if (nom.includes('conserve') || nom.includes('boîte')) {
            return 'Conserves';
          }
          if (nom.includes('pain') || nom.includes('baguette') || nom.includes('croissant')) {
            return 'Boulangerie';
          }
          if (nom.includes('chips') || nom.includes('biscuit') || nom.includes('gâteau') || nom.includes('chocolat') || nom.includes('bonbon')) {
            return 'Snacks';
          }
          if (nom.includes('surgelé') || nom.includes('glace')) {
            return 'Surgelés';
          }
          return 'Épicerie';
        }
        return '';
      };
      
      // Positions de départ
      let leftY = yByCol[0];
      let rightY = yByCol[1];

      const drawCategoryHeader = (categorie: string, suffix = '') => {
        ensureSpace(12);
        const x = xByCol[col];
        const y = yByCol[col];

        // Titre catégorie avec fond doré
        doc.setFillColor(nauticGold[0], nauticGold[1], nauticGold[2]);
        doc.rect(x - 2, y - 4, columnWidth + 4, 7, 'F');
        doc.setTextColor(nauticBlue[0], nauticBlue[1], nauticBlue[2]);
        doc.setFontSize(9);
        doc.text(`${categorie.toUpperCase()}${suffix}`, x, y);
        yByCol[col] += 10;
      };

      const drawSousCategorieHeader = (sousCategorie: string) => {
        ensureSpace(6);
        const x = xByCol[col];
        const y = yByCol[col];

        // Sous-titre (puce dorée)
        doc.setFillColor(nauticGold[0], nauticGold[1], nauticGold[2]);
        doc.circle(x + 1.5, y - 1.5, 1.5, 'F');
        doc.setFontSize(8);
        doc.setTextColor(nauticLight[0], nauticLight[1], nauticLight[2]);
        doc.text(sousCategorie, x + 5, y);
        yByCol[col] += 6;
      };
      
      // Articles critiques d'abord (colonne gauche, puis droite, puis pages)
      const articlesCritiques = articles.filter(a => a.priorite === 'critique');
      if (articlesCritiques.length > 0) {
        // Section titre (dans la colonne courante)
        ensureSpace(14);
        {
          const x = xByCol[col];
          const y = yByCol[col];
          doc.setFillColor(criticalRed[0], criticalRed[1], criticalRed[2]);
          doc.rect(x - 2, y - 4, columnWidth + 4, 8, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
          doc.text(t('pdf.criticalArticlesTitle'), x, y + 1);
          yByCol[col] += 12;
        }

        const critiquesParCategorie = articlesCritiques.reduce((acc, article) => {
          if (!acc[article.categorie]) acc[article.categorie] = [];
          acc[article.categorie].push(article);
          return acc;
        }, {} as Record<string, ArticleAvitaillement[]>);

        const categoriesCritiquesTriees = Object.entries(critiquesParCategorie).sort(
          ([catA], [catB]) =>
            getCategorieOrdre(catA) - getCategorieOrdre(catB) || catA.localeCompare(catB, 'fr'),
        );

        categoriesCritiquesTriees.forEach(([categorie, articlesCategorie]) => {
          // Petit titre catégorie
          ensureSpace(8);
          doc.setFontSize(9);
          doc.setTextColor(criticalRed[0], criticalRed[1], criticalRed[2]);
          doc.text(categorie.toUpperCase(), xByCol[col], yByCol[col]);
          yByCol[col] += 6;

          // Grouper par sous-catégorie si alimentation
          if (categorie.toLowerCase() === 'alimentation') {
            const parSousCategorie = articlesCategorie.reduce((acc, article) => {
              const sc = getSousCategorie(article) || 'Autre';
              if (!acc[sc]) acc[sc] = [];
              acc[sc].push(article);
              return acc;
            }, {} as Record<string, ArticleAvitaillement[]>);

            Object.entries(parSousCategorie).forEach(([sousCategorie, articlesSC]) => {
              const moved = ensureSpace(6);
              if (moved) {
                doc.setFontSize(9);
                doc.setTextColor(criticalRed[0], criticalRed[1], criticalRed[2]);
                doc.text(`${categorie.toUpperCase()} (suite)`, xByCol[col], yByCol[col]);
                yByCol[col] += 6;
              }
              doc.setFontSize(8);
              doc.setTextColor(nauticLight[0], nauticLight[1], nauticLight[2]);
              doc.text(sousCategorie, xByCol[col], yByCol[col]);
              yByCol[col] += 5;

              articlesSC.forEach((article) => {
                const movedArticle = ensureSpace(6);
                if (movedArticle) {
                  doc.setFontSize(9);
                  doc.setTextColor(criticalRed[0], criticalRed[1], criticalRed[2]);
                  doc.text(`${categorie.toUpperCase()} (suite)`, xByCol[col], yByCol[col]);
                  yByCol[col] += 6;
                  doc.setFontSize(8);
                  doc.setTextColor(nauticLight[0], nauticLight[1], nauticLight[2]);
                  doc.text(sousCategorie, xByCol[col], yByCol[col]);
                  yByCol[col] += 5;
                }
                drawArticle(article, xByCol[col], yByCol[col], true);
                yByCol[col] += 6;
              });

              yByCol[col] += 2;
            });
          } else {
            articlesCategorie.forEach((article) => {
              const moved = ensureSpace(6);
              if (moved) {
                doc.setFontSize(9);
                doc.setTextColor(criticalRed[0], criticalRed[1], criticalRed[2]);
                doc.text(`${categorie.toUpperCase()} (suite)`, xByCol[col], yByCol[col]);
                yByCol[col] += 6;
              }
              drawArticle(article, xByCol[col], yByCol[col], true);
              yByCol[col] += 6;
            });
          }

          yByCol[col] += 4;
        });

        // Synchroniser les colonnes après la section critique
        leftY = Math.max(yByCol[0], yByCol[1]);
        yByCol[0] = leftY;
        yByCol[1] = leftY;
        rightY = leftY;
      }
      
      // Articles normaux en deux colonnes
      const articlesNormaux = articles.filter(a => a.priorite === 'normale');
      const normauxParCategorie = articlesNormaux.reduce((acc, article) => {
        if (!acc[article.categorie]) acc[article.categorie] = [];
        acc[article.categorie].push(article);
        return acc;
      }, {} as Record<string, ArticleAvitaillement[]>);

      const categoriesNormalesTriees = Object.entries(normauxParCategorie).sort(
        ([catA], [catB]) =>
          getCategorieOrdre(catA) - getCategorieOrdre(catB) || catA.localeCompare(catB, 'fr'),
      );

      categoriesNormalesTriees.forEach(([categorie, articlesCategorie]) => {
        // Dessiner l'en-tête catégorie (et s'il y a un saut de colonne/page en plein milieu, on le redessine en "suite")
        drawCategoryHeader(categorie);

        if (categorie.toLowerCase() === 'alimentation') {
          const parSousCategorie = articlesCategorie.reduce((acc, article) => {
            const sc = getSousCategorie(article) || 'Autre';
            if (!acc[sc]) acc[sc] = [];
            acc[sc].push(article);
            return acc;
          }, {} as Record<string, ArticleAvitaillement[]>);

          Object.entries(parSousCategorie).forEach(([sousCategorie, articlesSC]) => {
            const movedSC = ensureSpace(8);
            if (movedSC) drawCategoryHeader(categorie, ' (suite)');
            drawSousCategorieHeader(sousCategorie);

            articlesSC.forEach((article) => {
              const moved = ensureSpace(6);
              if (moved) {
                drawCategoryHeader(categorie, ' (suite)');
                drawSousCategorieHeader(sousCategorie);
              }
              drawArticle(article, xByCol[col], yByCol[col], false);
              yByCol[col] += 6;
            });

            yByCol[col] += 3;
          });
        } else {
          articlesCategorie.forEach((article) => {
            const moved = ensureSpace(6);
            if (moved) drawCategoryHeader(categorie, ' (suite)');
            drawArticle(article, xByCol[col], yByCol[col], false);
            yByCol[col] += 6;
          });
        }

        yByCol[col] += 6;

        // Mise à jour des repères pour rester compatible avec la suite du code
        leftY = Math.max(yByCol[0], leftY);
        rightY = Math.max(yByCol[1], rightY);
      });
      
      // Pied de page sur toutes les pages
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Ligne dorée en bas
        doc.setFillColor(nauticGold[0], nauticGold[1], nauticGold[2]);
        doc.rect(0, 285, 210, 2, 'F');
        
        // Footer
        doc.setFillColor(nauticBlue[0], nauticBlue[1], nauticBlue[2]);
        doc.rect(0, 287, 210, 15, 'F');
        
        doc.setTextColor(nauticGold[0], nauticGold[1], nauticGold[2]);
        doc.setFontSize(10);
        doc.text(t('pdf.createdBy'), 105, 294, { align: 'center' });
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.text(t('pdf.pageNumber', { page: i, pages: pageCount }), 195, 294, { align: 'right' });
      }
      
      const fileName = `liste-achat-${liste.nom.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      doc.save(fileName);
      
      toast({
        title: t('listeAchatAvitaillement.success'),
        description: t('listeAchatAvitaillement.pdfSuccess'),
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      toast({
        title: t('listeAchatAvitaillement.error'),
        description: t('listeAchatAvitaillement.cannotGeneratePdf'),
        variant: "destructive",
      });
    }
  };

  if (articles.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{t('listeAchatAvitaillement.noArticles')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Grouper par catégorie
  const articlesCritiques = articles.filter(a => a.priorite === 'critique');
  const articlesNormaux = articles.filter(a => a.priorite === 'normale');

  const critiquesParCategorie = articlesCritiques.reduce((acc, article) => {
    if (!acc[article.categorie]) acc[article.categorie] = [];
    acc[article.categorie].push(article);
    return acc;
  }, {} as Record<string, ArticleAvitaillement[]>);

  const normauxParCategorie = articlesNormaux.reduce((acc, article) => {
    if (!acc[article.categorie]) acc[article.categorie] = [];
    acc[article.categorie].push(article);
    return acc;
  }, {} as Record<string, ArticleAvitaillement[]>);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-nautic" />
            {t('listeAchatAvitaillement.titleWithName', { name: liste.nom, count: articles.length })}
          </CardTitle>
          <div className="flex gap-2">
            <Button onClick={copierListeAchat} variant="outline" size="sm">
              <Copy className="h-4 w-4 mr-2" />
              {t('listeAchat.copy')}
            </Button>
            <Button onClick={genererPDF} className="bg-nautic hover:bg-nautic/90 text-white" size="sm">
              <FileText className="h-4 w-4 mr-2" />
              {t('listeAchat.createPdf')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground mb-4">
            {t('listeAchatAvitaillement.personsAndDays', { people: liste.nombre_personnes, days: liste.nombre_jours })}
          </div>
          
          <div className="space-y-6">
            {Object.keys(critiquesParCategorie).length > 0 && (
              <div>
                <h4 className="font-medium text-red-600 mb-4 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  {t('listeAchat.criticalArticles')}
                </h4>
                {Object.entries(critiquesParCategorie).map(([categorie, articlesCategorie]) => (
                  <div key={categorie} className="mb-4">
                    <h5 className="font-medium text-nautic mb-2 flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      {categorie}
                    </h5>
                    {articlesCategorie.map((article, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-red-50 rounded border-l-4 border-red-500 mb-2">
                        <span className="font-medium">{article.nom}</span>
                        <span className="text-red-600 font-medium">{article.quantite} {article.unite}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {Object.keys(normauxParCategorie).length > 0 && (
              <div>
                <h4 className="font-medium text-nautic mb-4 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  {t('listeAchatAvitaillement.articlesToBuy')}
                </h4>
                {Object.entries(normauxParCategorie).map(([categorie, articlesCategorie]) => (
                  <div key={categorie} className="mb-4">
                    <h5 className="font-medium text-nautic mb-2 flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      {categorie}
                    </h5>
                    {articlesCategorie.map((article, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-nautic/5 rounded border-l-4 border-nautic mb-2">
                        <span>{article.nom}</span>
                        <span className="text-nautic font-medium">{article.quantite} {article.unite}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ListeAchatAvitaillement;
